import React, {useCallback, useEffect, useRef, useState} from 'react';
import './App.css';
import axios from 'axios';

import {ReactComponent as LogoSVG} from './assets/logo.svg';
import {ReactComponent as HopkinsSVG} from './assets/Hopkins.svg';
import {ReactComponent as InverGroveSVG} from './assets/Inver Grove.svg';

import LoadingSpinner from './LoadingSpinner';
import PaySummaryModal from "./components/modals/PaySummaryModal";
import EarningsSummaryModal from "./components/modals/EarningsSummaryModal";
import EditEventModal from "./components/modals/EditEventModal";
import useModalStore from "./stores/modalStore";
import ActionButtons from "./components/buttons/ActionButtons";
import AddEventModal from "./components/modals/AddEventModal";
import CompedServiceModal from "./components/modals/CompedServiceModal";
import SickModal from "./components/modals/SickModal";
import TimeOffModal from "./components/modals/TimeOffModal";
import EventListModal from "./components/modals/EventListModal";
import SplitTipCalculatorModal from "./components/modals/SplitTipCalculatorModal";
import RunPayrollModal from "./components/modals/RunPayrollModal";
import EsstReportModal from "./components/modals/EsstReportModal";
import HandbookModal from "./components/modals/HandbookModal";
import EmployeeStatusTable from "./components/sections/EmployeeStatusTable";
import EventsTable from "./components/sections/EventsTable";
import getFirebaseAuthToken from "./services/fireBaseAuthenticationService";
import { getAuth } from "firebase/auth";
import {
    BACKEND_URL,
    LOCATION_NAME_TO_ID,
    LOCATION_ID_TO_NAME,
    STEPS,
    DEFAULT_INACTIVITY_TIMEOUT_SECONDS, BACKEND_CALL_TIMEOUT, USER_SIGNIN_TIMEOUT
} from "./utils/constants";
import RequestsTable from "./components/sections/RequestsTable";
import {fetchUserEventList, fetchUserPermissions, loggedAlert} from "./services/eventService";
import { toEpoch } from "./utils/formatting";
import useUserStore, {fetchAndSetEmployees} from "./stores/userStore";
import useEventStore from "./stores/eventStore";
import LastPeriodCharts from "./components/sections/LastPeriodCharts";
import CumulativeHoursChart from "./components/sections/CumulativeHoursChart";
import NoteSubmitForm from "./components/sections/NoteSubmitForm";
import { LogOut, MapPin, BarChart3, BarChart2, ClipboardList, Users, Clock, Settings, TrendingUp } from "lucide-react";
import useSettingsStore from "./stores/settingsStore";
import SettingsModal from "./components/modals/SettingsModal";
import DailySummaryModal from "./components/modals/DailySummaryModal";
import BusinessTrendsModal from "./components/modals/BusinessTrendsModal";
import EmployeeComparisonModal from "./components/modals/EmployeeComparisonModal";
import SeeScheduleModal from "./components/modals/SeeScheduleModal";
// import DaySmartAuthModal from "./components/modals/DaySmartAuthModal";
// import TicketListModal from "./components/modals/TicketListModal";

const Logo = React.memo(LogoSVG);
const Hopkins = React.memo(HopkinsSVG);
const InverGrove = React.memo(InverGroveSVG);

const assert = require('assert');

console.log("App Version:", process.env.REACT_APP_VERSION);
console.log("Backend url: ", process.env.REACT_APP_API_BASE_URL);

const TAB_CONFIG = [
    { key: "stats", label: "My Stats", icon: BarChart3 },
    { key: "requests", label: "Requests", icon: ClipboardList },
    { key: "team", label: "Team", icon: Users, privilegedOnly: true },
    { key: "events", label: "My Events", icon: Clock },
];

function HomePage() {
    const { currentUser, privileged, setCurrentUser, setPermissions, userIdToFirstName, employees } = useUserStore();
    const { selectedLocation, setSelectedLocation, setRequestList, setEmployeeEvents, requestList } = useUserStore();
    
    // Initialize step based on whether user is already authenticated
    const getInitialStep = () => {
        if (currentUser?.authenticated && selectedLocation && selectedLocation !== LOCATION_NAME_TO_ID.UNSELECTED) {
            return STEPS.DASHBOARD;
        }
        if (currentUser?.authenticated || currentUser?.user_id) {
            return STEPS.LOCATION;
        }
        return STEPS.IDENTIFY;
    };
    const [step, setStep] = useState(getInitialStep);
    const { selectedEvent, setSelectedEvent, clearSelectedEvent} = useEventStore();

    const [isOwnerSession, setIsOwnerSession] = useState(false);
    const [signinLoading, setSigninLoading] = useState(false);
    const inactivityRef = useRef(0);
    const [displayInactivity, setDisplayInactivity] = useState(0);
    const [historicPayrolls, setHistoricPayrolls] = useState([]);
    const [dashboardTab, setDashboardTab] = useState("stats");

    const userTimeout = Number.isFinite(currentUser?.inactivity_timeout_seconds)
        ? currentUser.inactivity_timeout_seconds
        : DEFAULT_INACTIVITY_TIMEOUT_SECONDS;

    const {
        showTimeOffModal,
        showAddEventModal,
        showEditEventModal,
        closeModal,
        closeAllModals,
        openModal,
    } = useModalStore();

    const { openSettings, blurPII } = useSettingsStore();
    const piiClass = blurPII ? 'blur-pii' : '';

    const passwordInputRef = useRef(null);

    const resetInactivityTimer = useCallback(() => {
        inactivityRef.current = 0;
    }, []);

    const resetSystem = useCallback(() => {
        setStep(STEPS.IDENTIFY);
        setCurrentUser({});
        setSelectedLocation(LOCATION_NAME_TO_ID.UNSELECTED);
        closeAllModals();
        clearSelectedEvent();
        inactivityRef.current = 0;
        setDashboardTab("stats");
    }, [clearSelectedEvent, setCurrentUser, closeAllModals, setSelectedLocation]);

    // Inactivity timer — uses a ref to avoid re-rendering every second.
    // Only triggers a re-render when the timeout fires (resetSystem).
    useEffect(() => {
        const interval = setInterval(() => {
            if (useUserStore.getState().keepAwakeCount > 0) {
                inactivityRef.current = 0;
                return;
            }
            inactivityRef.current += 1;
            // Update footer display every 10s (debug only, not perf-critical)
            if (inactivityRef.current % 10 === 0) {
                setDisplayInactivity(inactivityRef.current);
            }
            if (inactivityRef.current > userTimeout) {
                resetSystem();
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [userTimeout, resetSystem]);

    // Auto-login: skip employee sign-in for owner emails
    useEffect(() => {
        const OWNER_EMAILS = ['josh@zoomgroomllc.com', 'joshuastowell25@gmail.com'];
        const OWNER_USER_ID = 153;

        if (step !== STEPS.IDENTIFY) return;

        const firebaseUser = getAuth().currentUser;
        if (firebaseUser && OWNER_EMAILS.includes(firebaseUser.email?.toLowerCase())) {
            setIsOwnerSession(true);
            setCurrentUser({ user_id: OWNER_USER_ID, first_name: 'Josh', inactivity_timeout_seconds: 30 });
            setStep(STEPS.LOCATION);
            processAuthenticate(OWNER_USER_ID, null);
        }
    }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleLoginAs = (targetUserId) => {
        const uid = parseInt(targetUserId, 10);
        if (!uid || uid === currentUser?.user_id) return;
        setCurrentUser({ ...currentUser, user_id: uid, first_name: userIdToFirstName[uid] || `Employee ${uid}`, authenticated: false });
        processAuthenticate(uid, null);
    };

    const completeLocationSelection = (location_id) => {
        if (location_id) {
            setSelectedLocation(location_id);
            setSelectedEvent({...selectedEvent, location_id: location_id});
            setStep(STEPS.DASHBOARD);
            assert(currentUser.user_id != null, "User id must not be null, user_id is: "+ currentUser.user_id)
        } else {
            loggedAlert('Please select a location!');
        }
    };

    const handleSigninSubmit = async (e) => {
        e.preventDefault();
        setSigninLoading(true);

        const identifierInput = e.target.elements['employee_id'].value.trim();
        const passwordInput = e.target.elements['password'].value;
        const isEmail = identifierInput.includes('@');
        const userIdInput = isEmail ? null : parseInt(identifierInput);
        const emailInput = isEmail ? identifierInput : null;

        try {
            setStep(STEPS.LOCATION);
            setCurrentUser({...currentUser, user_id: userIdInput || identifierInput, first_name: identifierInput, inactivity_timeout_seconds: 30}); //Allow them to move forward in the meantime
            processAuthenticate(userIdInput, passwordInput, emailInput); //Choosing not to await this any longer
        } catch (err) {
            console.error("Login failed", err);
            resetSystem()
            alert("Bad username or password (or internet issues). Please try again.")
        } finally {
            setSigninLoading(false); // Optional if you redirect after success
        }
    };


    const processAuthenticate = async (uid, pw, email = null) => {
        const payload = {
            password: pw,
        };
        if (email) {
            payload.email = email;
        } else {
            payload.user_id = uid;
        }

        try {
            const token = await getFirebaseAuthToken();

            const response = await axios.post(`${BACKEND_URL}/authenticate`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: USER_SIGNIN_TIMEOUT
            });

            if (response.status === 200) {

                const employee = email
                    ? response.data.find(e => e.email?.toLowerCase() === email.toLowerCase())
                    : response.data.find(e => e.user_id === uid);

                if (!employee) {
                    console.error(`No employee found with user_id: ${uid}`);
                    return;
                }

                const resolvedUid = employee.user_id;
                setCurrentUser({
                    ...currentUser,
                    position: employee.position,
                    birth_date: employee.birth_date,
                    user_id: resolvedUid,
                    first_name: employee.first_name,
                    inactivity_timeout_seconds: employee.inactivity_timeout_seconds,
                    authenticated: true
                });

                //NOTE: react state updates (including currentUser) are ASYNC. So you can't set these things and then reference currentUser right away.
                fetchAndSetEmployees(response.data)
                fetchUserEventList(resolvedUid, setEmployeeEvents, "processAuthenticate"); //TODO: make authenticate just return employee events WITH employee data and always keep it hot and ready on the cache in the backend

                // Fetch granular permissions from Postgres-backed endpoint
                fetchUserPermissions(resolvedUid)
                    .then(data => setPermissions(data.permissions || []))
                    .catch(err => console.warn("Failed to fetch permissions, falling back to position-based:", err));
            } else if (response.status === 401 || response.status === 400){ //Legitimately entered bad password or no password, backend succeeded and behaved 100% as expected
                resetSystem()
                alert(`Invalid username/password combination. Try again.`)
            } else {
                resetSystem()
                loggedAlert(`Invalid username/password combination. Try again. HTTP status code: ${response.status}`, uid);
            }

        } catch (error) {
            resetSystem();
            console.error('Error authenticating:', error);

            const rawError = error?.response?.data?.error;
            const errorMessage = error?.response?.data?.details || rawError || error.message || "Unknown error occurred";

            if (typeof rawError === "string" && rawError.toLowerCase().includes("bad user id or password")) {
                alert("Incorrect user ID or password. Please try again.");
            } else if (typeof rawError === "string" && (rawError.toLowerCase().includes("invalid user id") || rawError.toLowerCase().includes("invalid user id or email"))) {
                alert("Invalid user ID or email. Please check your credentials and try again.");
            } else {
                loggedAlert(`Auth endpoint failure. Internet issues? Please try again.\n${errorMessage}`, uid);
            }
        }

    };

    //When a super user updates an existing event for an employee
    const handleUpdateEvent = async (e, successCallback, failCallback) => {
        e.preventDefault();

        const payload = {
            id: selectedEvent.id,
            user_id: selectedEvent.user_id,
            event_type: selectedEvent.event_type,
            event_timestamp: toEpoch(selectedEvent.event_timestamp),
            insert_timestamp: Date.now(),
            notes: selectedEvent.notes,
            created_by: currentUser.user_id,
            location_id: selectedEvent.location_id,
            sequence_number: selectedEvent.sequence_number,
            needs_review: selectedEvent.needs_review
        };

        try {
            // Send the updated event as a new record to the POST endpoint
            const token = await getFirebaseAuthToken();

            const response = await axios.post(`${BACKEND_URL}/timesheet`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: BACKEND_CALL_TIMEOUT
            });

            if (response.status === 200) {
                const newlyAddedEvent = response.data;
                setEmployeeEvents(selectedEvent.user_id, prev => [newlyAddedEvent, ...prev]);
                successCallback();
                closeModal("editEventModal");
            } else {
                failCallback();
                loggedAlert(`Error: ${response.data.error}`, currentUser?.user_id, selectedEvent?.location_id);
            }

        } catch (error) {
            console.error('Error updating event:', error);

            const errorMessage = error?.response?.data?.details || error?.message || "Unknown error occurred";
            loggedAlert(`An error occurred while updating event: ${errorMessage}`, currentUser?.user_id, selectedEvent?.location_id);
        }

        setSelectedEvent(prev => ({...prev, notes: ""})); // Clear notes the RIGHT way, react doesn't work as intended if you edit state without using the setters
    };


    const logTimeOffEvent = async () => {
        const created_by = currentUser.user_id;
        let user_requesting_time_off = currentUser.user_id;

        if (showTimeOffModal) {
            user_requesting_time_off = currentUser.user_id;
        } else if (showAddEventModal || showEditEventModal) {
            user_requesting_time_off = selectedEvent.user_id;
        }

        if (!selectedEvent?.event_timestamp || !selectedEvent?.event_end_timestamp) {
            loggedAlert("Please select both a start and end time for the time off.", currentUser?.user_id, selectedEvent?.location_id || 1, true, false);
            return;
        }

        if (selectedEvent.event_end_timestamp <= selectedEvent.event_timestamp) {
            loggedAlert("End time must be after start time.", currentUser?.user_id, selectedEvent?.location_id || 1, true, false);
            return;
        }

        if(!selectedEvent.location_id){
            loggedAlert("Please select a location.", currentUser?.user_id, selectedEvent?.location_id || 1, true, false);
            return;
        }

        const payload = {
            event_type: "time off",
            event_timestamp: selectedEvent.event_timestamp,
            event_end_timestamp: selectedEvent.event_end_timestamp,
            insert_timestamp: Date.now(),
            notes: selectedEvent?.notes || "",
            created_by: created_by,
            user_id: user_requesting_time_off,
            location_id: selectedEvent.location_id,
            needs_review: true
        };

        try {
            const token = await getFirebaseAuthToken();

            const response = await axios.post(`${BACKEND_URL}/timesheet`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                timeout: BACKEND_CALL_TIMEOUT
            });

            if (response.status === 200) {
                const newlyAddedEvent = response.data;
                setEmployeeEvents(currentUser.user_id, prev => [newlyAddedEvent, ...prev]);
                setRequestList(prev => [newlyAddedEvent, ...prev]);
                closeModal("timeOffModal");
            } else {
                loggedAlert(`Error: ${response.data.error}`);
            }
        } catch (error) {
            console.error('Error logging time off:', error);
            const errorMessage = error?.response?.data?.details || error?.message || "Unknown error occurred";
            loggedAlert(`${user_requesting_time_off}: An error occurred while logging the time off event: ${errorMessage}`);
        }

        setSelectedEvent(prev => ({ ...prev, notes: "", event_end_timestamp: null }));
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            const windowHeight = window.innerHeight;
            const y = e.clientY;

            // If mouse is within 50px of bottom, show footer
            if (windowHeight - y < 50) {
                document.body.classList.add('show-footer');
            } else {
                document.body.classList.remove('show-footer');
            }
        };

        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    const locationName = LOCATION_ID_TO_NAME[selectedLocation] || "Unknown";
    const displayName = userIdToFirstName[currentUser?.user_id] || currentUser?.user_id;
    const pendingRequestCount = requestList?.length || 0;

    const visibleTabs = TAB_CONFIG.filter(t => !t.privilegedOnly || privileged);

    return (
        <div
            onMouseMove={resetInactivityTimer}
            onKeyDown={resetInactivityTimer}
            onClick={resetInactivityTimer}
            className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 text-gray-800"
        >
            {/* ── SIGN-IN STEP ── */}
            {step === STEPS.IDENTIFY && (
                <div className="flex-1 flex flex-col items-center justify-center px-4">
                    <Logo className="h-24 w-auto mb-8 drop-shadow-md" />
                    <div className="w-full max-w-sm bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
                        <h2 className="text-xl font-bold text-center text-gray-800 mb-6">Employee Sign In</h2>
                        <form onSubmit={handleSigninSubmit} autoComplete="on" className="space-y-4">
                            {!signinLoading ? (
                                <>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Employee ID or Email</label>
                                        <input
                                            name="employee_id"
                                            placeholder="Enter your ID or email"
                                            autoComplete="username"
                                            autoFocus
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Password</label>
                                        <input
                                            type="password"
                                            name="password"
                                            placeholder="Enter password"
                                            autoComplete="current-password"
                                            ref={passwordInputRef}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 outline-none transition"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className="!w-full !py-3 !bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !rounded-xl !border-none !shadow-md hover:!shadow-lg transition-all"
                                    >
                                        Sign In
                                    </button>
                                </>
                            ) : (
                                <div className="flex justify-center py-6"><LoadingSpinner /></div>
                            )}
                        </form>
                    </div>
                </div>
            )}

            {/* ── LOCATION STEP ── */}
            {step === STEPS.LOCATION && (
                <div className="flex-1 flex flex-col items-center justify-center px-4 overflow-hidden">
                    <Logo className="h-14 sm:h-20 w-auto max-w-[80vw] mb-6 drop-shadow-md" />
                    <h2 className="text-lg font-bold text-gray-700 mb-6">Select Your Location</h2>
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 w-full max-w-md sm:max-w-none sm:w-auto">
                        <button
                            className={`!bg-white !border-2 !rounded-2xl !p-4 sm:!p-6 !shadow-md hover:!shadow-xl hover:!border-indigo-400 transition-all ${
                                selectedLocation === LOCATION_NAME_TO_ID.Hopkins ? '!border-indigo-500 !shadow-xl !ring-2 !ring-indigo-200' : '!border-gray-200'
                            }`}
                            onClick={() => completeLocationSelection(LOCATION_NAME_TO_ID.Hopkins)}
                        >
                            <Hopkins className="w-full sm:w-52 h-auto" />
                        </button>
                        <button
                            className={`!bg-white !border-2 !rounded-2xl !p-4 sm:!p-6 !shadow-md hover:!shadow-xl hover:!border-indigo-400 transition-all ${
                                selectedLocation === LOCATION_NAME_TO_ID.InverGrove ? '!border-indigo-500 !shadow-xl !ring-2 !ring-indigo-200' : '!border-gray-200'
                            }`}
                            onClick={() => completeLocationSelection(LOCATION_NAME_TO_ID.InverGrove)}
                        >
                            <InverGrove className="w-full sm:w-64 h-auto" />
                        </button>
                    </div>
                </div>
            )}

            {/* ── DASHBOARD STEP ── */}
            {step === STEPS.DASHBOARD && (
                <>
                    {/* Sticky Top Bar */}
                    <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-200/60 shadow-sm">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-3">
                            <div className="basis-full sm:basis-auto flex justify-center sm:justify-start">
                                <Logo className="h-8 sm:h-10 w-auto" />
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3">
                                <span className={`text-xs sm:text-sm font-semibold text-gray-700 ${piiClass}`}>
                                    {displayName}
                                </span>
                                <span className="inline-flex items-center gap-1 text-xs font-medium bg-indigo-50 text-indigo-700 px-2 sm:px-2.5 py-1 rounded-full border border-indigo-100">
                                    <MapPin size={12} />
                                    {locationName}
                                </span>
                            </div>
                            <div className="flex items-center gap-2 ml-auto">
                                {isOwnerSession && employees.length > 0 && (
                                    <select
                                        value={currentUser?.user_id || ''}
                                        onChange={(e) => handleLoginAs(e.target.value)}
                                        className="!text-xs !font-medium !text-gray-600 !bg-white !border !border-gray-200 !rounded-lg !px-2 !py-1.5 hover:!border-indigo-300 focus:!border-indigo-400 focus:!ring-1 focus:!ring-indigo-200 !outline-none !cursor-pointer"
                                        title="Login as another employee"
                                    >
                                        {employees
                                            .filter(e => !e.end_date)
                                            .sort((a, b) => a.first_name.localeCompare(b.first_name))
                                            .map(emp => (
                                                <option key={emp.user_id} value={emp.user_id}>
                                                    {emp.first_name} ({emp.user_id})
                                                </option>
                                            ))}
                                    </select>
                                )}
                                {privileged && (
                                    <button
                                        onClick={() => openModal('dailySummaryModal')}
                                        className="!inline-flex !items-center !gap-1.5 !px-2 sm:!px-3 !py-1.5 !text-xs !font-medium !text-gray-600 hover:!text-indigo-700 !bg-transparent !border !border-gray-200 hover:!border-indigo-300 hover:!bg-indigo-50 !rounded-lg transition-colors"
                                        title="Daily Business Summary"
                                    >
                                        <TrendingUp size={14} />
                                        <span className="hidden sm:inline">Daily Summary</span>
                                    </button>
                                )}
                                {privileged && (
                                    <button
                                        onClick={() => openModal('businessTrendsModal')}
                                        className="!inline-flex !items-center !gap-1.5 !px-2 sm:!px-3 !py-1.5 !text-xs !font-medium !text-gray-600 hover:!text-indigo-700 !bg-transparent !border !border-gray-200 hover:!border-indigo-300 hover:!bg-indigo-50 !rounded-lg transition-colors"
                                        title="Business Trends"
                                    >
                                        <BarChart2 size={14} />
                                        <span className="hidden sm:inline">Trends</span>
                                    </button>
                                )}
                                <button
                                    onClick={openSettings}
                                    className="!p-1.5 !bg-transparent !border !border-gray-200 !rounded-lg hover:!bg-gray-100 hover:!border-gray-300 !text-gray-400 hover:!text-gray-600 !transition-colors"
                                    title="Settings"
                                >
                                    <Settings size={16} />
                                </button>
                                <button
                                    onClick={resetSystem}
                                    className="!inline-flex !items-center !gap-1.5 !px-2 sm:!px-3 !py-1.5 !text-xs !font-medium !text-gray-500 hover:!text-red-600 !bg-transparent !border !border-gray-200 hover:!border-red-200 !rounded-lg transition-colors"
                                >
                                    <LogOut size={14} />
                                    <span className="hidden sm:inline">Log Out</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Dashboard Content */}
                    <main className="flex-1 overflow-y-auto">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
                            {/* Hero Row: Clock + Quick Actions */}
                            <ActionButtons handleLogout={resetSystem} />

                            {/* Note Form */}
                            <NoteSubmitForm />

                            {/* Tab Navigation */}
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 overflow-hidden">
                                <div className="flex border-b border-gray-100 overflow-x-auto">
                                    {visibleTabs.map(tab => {
                                        const Icon = tab.icon;
                                        const isActive = dashboardTab === tab.key;
                                        const badge = tab.key === "requests" && pendingRequestCount > 0 ? pendingRequestCount : null;
                                        return (
                                            <button
                                                key={tab.key}
                                                onClick={() => setDashboardTab(tab.key)}
                                                className={`!flex !items-center !gap-2 !px-5 !py-3.5 !text-sm !font-medium !border-b-2 !border-none !rounded-none !bg-transparent whitespace-nowrap transition-colors ${
                                                    isActive
                                                        ? '!border-b-indigo-600 !text-indigo-700 !bg-indigo-50/50'
                                                        : '!border-b-transparent !text-gray-500 hover:!text-gray-700 hover:!bg-gray-50'
                                                }`}
                                            >
                                                <Icon size={16} />
                                                {tab.label}
                                                {badge && (
                                                    <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 text-[10px] font-bold text-white bg-red-500 rounded-full">
                                                        {badge}
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>

                                <div className="p-0">
                                    {dashboardTab === "stats" && (
                                        <div className="space-y-4 p-4 sm:p-6">
                                            <CumulativeHoursChart />
                                            <LastPeriodCharts />
                                        </div>
                                    )}
                                    {dashboardTab === "requests" && <RequestsTable />}
                                    {dashboardTab === "team" && privileged && <EmployeeStatusTable />}
                                    {dashboardTab === "events" && <EventsTable userId={currentUser.user_id} />}
                                </div>
                            </div>
                        </div>
                    </main>
                </>
            )}

            {/* ── Modals ── */}
            <PaySummaryModal historicPayrolls={historicPayrolls} setHistoricPayrolls={setHistoricPayrolls} />
            <EarningsSummaryModal historicPayrolls={historicPayrolls} setHistoricPayrolls={setHistoricPayrolls} />
            <EventListModal />
            <AddEventModal />
            <SickModal />
            <TimeOffModal logTimeOffEvent={logTimeOffEvent} />
            <CompedServiceModal />
            <EditEventModal handleUpdateEvent={handleUpdateEvent} />
            <SplitTipCalculatorModal />
            <RunPayrollModal />
            <EsstReportModal />
            <HandbookModal />
            <DailySummaryModal />
            <BusinessTrendsModal />
            <EmployeeComparisonModal />
            <SeeScheduleModal />
            <SettingsModal />

            {/* Hidden footer - revealed on hover near bottom */}
            <footer className="bg-gray-200 text-center p-4 text-sm">
                <div>Inactivity Timer: ~{displayInactivity} seconds</div>
                <div>Version: {process.env.REACT_APP_VERSION}</div>
            </footer>
        </div>
    );
}

export default HomePage;
